import { useEffect, useState } from 'react';
import { MdKeyboardArrowRight } from 'react-icons/md';
import { Link, useNavigate } from 'react-router-dom';
import './Style.css';
import { toast } from 'react-toastify';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { useAuth } from '../../../../hooks/useAuth';
import { companyUserPermissions } from '../../../../enums/companyUserPermissions';
import { userHasPermission } from '../../../../utils/verifyUserPermission';
import RequestLimitTedModal from './RequestLimitTedModal';
import { useLimit } from '../../../../hooks/useLimit';
import { LimitOperationTypes, LimitRequestJustifications, LimitTypes } from '../../../../enums/accountLimitsEnums';
import { uploadS3 } from '../../../../services/uploadS3';
import { fileToBase64 } from '../../../../utils/fileToBase64';
import { getDocumentFormat } from '../../../../utils/documentFormat';
import { documentTypes } from '../../../../services/enums';
import { apiV1 } from '../../../../lib/axios';

const LimitesTed = () => {
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [disableRequestModal, setDisableRequestModal] = useState(false);
  const [hasChangedLimits, setHasChangedLimits] = useState(false);

  const { user } = useAuth();

  const { originalLimits, currentLimits, limits, setLimits, loading, fetchLimits, updateLimits, fetchLimitIncreaseRequests, limitIncreaseRequests } = useLimit();

  const labelTypeMap = {
    daily: 'Limite diário',
    transaction: 'Limite de transação',
  }

  const handleChange = (type, value) => {
    setLimits((prev) => ({
      ...prev,
      ted: {
        ...prev.ted,
        [type]: value
      }
    }));
  };

  const handleSubmit = async () => {
    await updateLimits([
      {
        type: LimitTypes.DAILY,
        newValue: limits.ted.daily,
        justification: LimitRequestJustifications.BUSINESS,
        operationType: LimitOperationTypes.TED
      },
      {
        type: LimitTypes.TRANSACTION,
        newValue: limits.ted.transaction,
        justification: LimitRequestJustifications.BUSINESS,
        operationType: LimitOperationTypes.TED
      }
    ]);
  };

  const handleRequestLimit = async (data) => {
    const { limits, justification, file } = data;
    if (!justification) {
      toast.error('Justificativa obrigatória');
      return;
    }

    const hasInvalidValue = Object.entries(limits).some(([key, value]) => {
      const currentValue = originalLimits.ted[key];
      return value && value < currentValue;
    });

    if (hasInvalidValue) {
      toast.error('Valor não pode ser menor que o atual');
      return;
    }

    try {
      if (file) await processFile(file);

      const limitTypeMap = {
        daily: LimitTypes.DAILY,
        transaction: LimitTypes.TRANSACTION,
      };

      const payload = Object.entries(limits)
      .filter(([_, value]) => value && value > 0)
      .map(([key, value]) => ({
        type: limitTypeMap[key],
        newValue: value,
        justification,
        operationType: LimitOperationTypes.TED
      }));
      
      const success = await updateLimits(payload);
      if (!success) return;

      setIsOpenModal(false);
    } catch (err) {
      console.log(err);
      toast.error('Erro ao solicitar limite');
    } finally {
      fetchLimitIncreaseRequests();
    }
  }

  const processFile = async (file) => {
    const isValid = validateFile(file);

    if(isValid){
      const documentBase64 = await fileToBase64(file);
      const fileType = file.type.split('/')[1];

      const documentFormat = getDocumentFormat(fileType);
      const documentUrl = await uploadS3(`comprovante-renda-${Date.now()}.${fileType}`, documentBase64);

      await apiV1.post('/document/store/update', {
        documentFile: documentUrl,
        documentFormat: documentFormat,
        documentName: 'Comprovante Renda',
        documentType: documentTypes.compRenda,
        description: 'Comprovante Renda',
      });
    } else {
      toast.error('Por favor, escolha um arquivo com extensão .jpg, .jpeg, .png ou .pdf.');
    }
  };

  const validateFile = (value) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];

    if (!value) {
      toast.error('Por favor, escolha um arquivo.');
      return false;
    }

    if (!allowedTypes.includes(value.type)) {
      toast.error('Tipo de arquivo não permitido.');
      return false;
    }

    return true;
  };

  const checkHasChangedLimits = () => {
    if (!userHasPermission(user, companyUserPermissions.UPDATE_TED_LIMITS)) return;
    
    const hasChanged = Object.keys(limits.ted).some(key => {
      return limits.ted[key] !== currentLimits.ted[key];
    });
    setHasChangedLimits(hasChanged);
  };

  useEffect(() => {
    checkHasChangedLimits();
  }, [limits]);

  const navigate = useNavigate();
  useEffect(() => {
    if (!(user && Object.keys(user).length > 0)) return;

    if (!userHasPermission(user, companyUserPermissions.VIEW_TED_LIMITS)) {
      toast.error('Você não tem permissão para acessar esta funcionalidade.');
      navigate('/pagina-ted');
    }
  }, [user]);

  useEffect(() => {
    fetchLimits();
    fetchLimitIncreaseRequests();
  }, []);

  useEffect(() => {
    if (limitIncreaseRequests?.length > 0) {
      const requiredTypes = ['daily', 'transaction'];

      const typeMap = {
        daily: '0',
        transaction: '4',
      };

      const tedRequests = limitIncreaseRequests.filter(request => Number(request.requestedOperationType) === LimitOperationTypes.TED);
      const foundTypes = new Set(tedRequests.map(req => req.requestedType));

      const hasAllTypes = requiredTypes.every(type => foundTypes.has(typeMap[type]));

      setDisableRequestModal(hasAllTypes);
    }
  }, [limitIncreaseRequests])

  return (
    <>
      <div className="limites-ted">
        <div className="limites-ted-container col-md-10">
          <div className="header">
            <div className="col-xl-12">
              <label>
                <Link to={'/pagina-principal'}>
                  Pagina inicial
                  <MdKeyboardArrowRight size={20} />
                </Link>
                <Link to={'/pagina-ted'}>
                  TED
                  <MdKeyboardArrowRight size={20} />
                </Link>
                Meus Limites TED
              </label>
              <p>Ajustar Limites TED</p>
            </div>
          </div>
          <div className="body">
            {['daily', 'transaction'].map((type) => {
              const max = originalLimits.ted[type] || 0;
              const value = limits.ted[type] || 0;
              const percent = max > 0 ? (value / max) * 100 : 0;

              return (
                <div className="limit-group" key={type}>
                  <label>{labelTypeMap[type]}</label>
                  <input
                    type="range"
                    min={0}
                    max={max}
                    step={10}
                    value={value}
                    onChange={(e) => handleChange(type, Number(e.target.value))}
                    disabled={loading || !userHasPermission(user, companyUserPermissions.UPDATE_TED_LIMITS)}
                    style={{
                      '--value-percent': `${percent}%`,
                      ...(!userHasPermission(user, companyUserPermissions.UPDATE_TED_LIMITS) && { cursor: 'not-allowed', opacity: 0.5 })
                    }}
                    className="styled-range"
                    title={!userHasPermission(user, companyUserPermissions.UPDATE_TED_LIMITS) ? 'Você não tem permissão para ajustar este limite' : ''}
                  />
                  <p className="limit-hint">
                    Máximo permitido para ajuste direto:
                    {loading
                      ? ' Carregando...'
                      : ` R$ ${Number(max)?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  </p>
                  <div className="limit-values">
                    <span>
                      <strong>Atual:</strong>{' '}
                      {loading
                        ? 'Carregando...'
                        : `R$ ${Number(currentLimits.ted[type])?.toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}`}
                    </span>
                    <span>
                      <strong>Novo:</strong>{' '}
                      {loading
                        ? 'Carregando...'
                        : `R$ ${Number(value)?.toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}`}
                    </span>
                  </div>
                </div>
              );
            })}

            {
              !loading && userHasPermission(user, companyUserPermissions.UPDATE_TED_LIMITS) &&
              <div className={`request-limit-increase ${disableRequestModal ? 'disabled' : ''}`} onClick={() => !disableRequestModal && setIsOpenModal(true)}>
                <AiOutlineInfoCircle size={24} color="var(--primary-color)" />
                <div>
                  {
                    disableRequestModal ? (
                      <>
                        <h4>Solicitação já realizada</h4>
                        <p>
                          Você já solicitou um aumento de limite nos últimos 90 dias.  
                          Será possível fazer uma nova solicitação após esse período.
                        </p>
                      </>
                    ) : (
                      <>
                        <h4>Solicitar Aumento de Limite</h4>
                        <p>
                          Precisa de um valor maior? Faça uma solicitação personalizada com
                          justificativa.
                        </p>
                      </>
                    )
                  }
                </div>
              </div>
            }

            <button 
              className="submit-button" 
              onClick={loading || !hasChangedLimits || !userHasPermission(user, companyUserPermissions.UPDATE_TED_LIMITS) ? () => { } : handleSubmit} 
              disabled={loading || !hasChangedLimits}
            >
              Aplicar ajustes de limite
            </button>

          </div>
        </div>
      </div>
      <RequestLimitTedModal
        isOpenModal={isOpenModal}
        setIsOpenModal={setIsOpenModal}
        loading={loading}
        handleRequestLimit={handleRequestLimit}
        originalLimits={originalLimits.ted}
        limitIncreaseRequests={limitIncreaseRequests}
      />
    </>
  );
};

export default LimitesTed;
